import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Save, Check, Loader2 } from 'lucide-react';

export default function FloatingSaveButton({ 
  hasChanges, 
  isSaving, 
  onSave, 
  className = '' 
}) {
  const buttonVariants = {
    inactive: {
      scale: 0.95,
      opacity: 0.5,
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)'
    },
    active: {
      scale: 1,
      opacity: 1,
      boxShadow: [
        '0 8px 24px rgba(16, 185, 129, 0.4)',
        '0 8px 32px rgba(16, 185, 129, 0.7)',
        '0 8px 24px rgba(16, 185, 129, 0.4)'
      ],
      transition: {
        boxShadow: {
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }
      }
    },
    saving: {
      scale: 0.98,
      opacity: 0.9
    },
    hover: {
      scale: 1.05,
      y: -2
    }
  };

  const getButtonState = () => {
    if (isSaving) return 'saving';
    if (hasChanges) return 'active';
    return 'inactive';
  };

  const getButtonColor = () => {
    if (isSaving) return 'bg-yellow-500';
    if (hasChanges) return 'bg-green-500 hover:bg-green-600';
    return 'bg-gray-400';
  };

  return (
    <motion.button
      className={`fixed bottom-6 left-6 z-40 px-4 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2 transition-all shadow-lg border ${
        isSaving 
          ? 'bg-amber-50 text-amber-700 border-amber-200' 
          : hasChanges 
          ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700' 
          : 'bg-white/90 text-gray-600 border-gray-200 backdrop-blur-sm'
      } ${className}`}
      whileHover={hasChanges && !isSaving ? { scale: 1.02 } : undefined}
      whileTap={hasChanges && !isSaving ? { scale: 0.98 } : undefined}
      onClick={hasChanges && !isSaving ? onSave : undefined}
      disabled={!hasChanges || isSaving}
      style={{
        cursor: hasChanges && !isSaving ? 'pointer' : 'default'
      }}
    >
      <AnimatePresence mode="wait">
        {isSaving ? (
          <motion.div
            key="saving"
            initial={{ rotate: 0 }}
            animate={{ rotate: 360 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          >
            <Loader2 size={16} />
          </motion.div>
        ) : hasChanges ? (
          <motion.div
            key="save"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
          >
            <Save size={16} />
          </motion.div>
        ) : (
          <motion.div
            key="check"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
          >
            <Check size={16} />
          </motion.div>
        )}
      </AnimatePresence>
      
      <span>
        {isSaving ? 'Сохранение...' : hasChanges ? 'Сохранить' : 'Сохранено'}
      </span>
    </motion.button>
  );
}
